/**
 * ActiveChallengeSection - Shows user's active challenge progress
 * Design: Screen 3 from home-screen-revamp, Frame 26
 *
 * Layout (L-shaped):
 * - Left: Dark card (#0E2317) with progress ring + "Your progress"
 * - Right top: "Ongoing Challenge #1:" + challenge name (green)
 * - Right bottom: Green stats card (#20F219) with Kcal burned
 */

import { useEffect, useState } from "react";
import { HomeChallengeCard } from "@/api/home";

interface ActiveChallengeSectionProps {
  challenge: HomeChallengeCard;
  challengeNumber?: number;
  onChallengeClick: (id: string) => void;
}

// Helper to get encouragement text based on progress
function getEncouragementText(percentage: number): string {
  if (percentage >= 90) return "Almost there!";
  if (percentage >= 70) return "You are almost there!";
  if (percentage >= 50) return "Halfway there!";
  if (percentage >= 25) return "Great progress!";
  return "Keep going!";
}

export function ActiveChallengeSection({
  challenge,
  challengeNumber = 1,
  onChallengeClick,
}: ActiveChallengeSectionProps) {
  const { progressPercentage, name, dailyGoal, daysCompleted } = challenge;

  // Animated progress state - starts at 0 and animates to actual value
  const [animatedProgress, setAnimatedProgress] = useState(0);
  const [displayedPercentage, setDisplayedPercentage] = useState(0);

  // Animate progress on mount
  useEffect(() => {
    // Small delay to ensure the initial render happens first
    const timer = setTimeout(() => {
      setAnimatedProgress(progressPercentage);
    }, 100);

    return () => clearTimeout(timer);
  }, [progressPercentage]);

  // Animate the percentage number counting up
  useEffect(() => {
    if (animatedProgress === 0) return;

    const duration = 1000; // 1 second
    const steps = 60;
    const stepDuration = duration / steps;
    const increment = progressPercentage / steps;
    let currentStep = 0;

    const interval = setInterval(() => {
      currentStep++;
      if (currentStep >= steps) {
        setDisplayedPercentage(progressPercentage);
        clearInterval(interval);
      } else {
        setDisplayedPercentage(Math.round(increment * currentStep));
      }
    }, stepDuration);

    return () => clearInterval(interval);
  }, [animatedProgress, progressPercentage]);

  // Estimate calories: ~60 kcal per km run
  const estimatedDistance = dailyGoal * daysCompleted;
  const estimatedCalories = Math.round(estimatedDistance * 60);

  const encouragement = getEncouragementText(progressPercentage);

  // Progress ring SVG properties
  const ringSize = 120;
  const strokeWidth = 12;
  const radius = (ringSize - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const strokeDashoffset = circumference - (animatedProgress / 100) * circumference;

  // Format today's date
  const today = new Date();
  const formattedDate = today.toLocaleDateString("en-US", {
    weekday: "long",
    month: "short",
    day: "numeric",
  });

  return (
    <div className="relative w-full px-4 pt-6 pb-4">
      {/* Date header */}
      <p className="text-white text-sm font-medium mb-4">{formattedDate}</p>

      {/* Clickable challenge card */}
      <div
        className="cursor-pointer"
        onClick={() => onChallengeClick(challenge.id)}
      >
        {/* Container for the L-shaped layout */}
        <div className="relative flex">
        {/* Left - Dark progress card */}
        <div className="w-[160px] min-h-[200px] bg-[#0e2317] rounded-tl-[15px] rounded-tr-[15px] rounded-bl-[15px] flex flex-col items-center pt-3 pb-3 px-3">
          {/* Title */}
          <h3 className="text-white text-sm font-medium text-center mb-2">
            Your progress
          </h3>

          {/* Progress Ring */}
          <div className="relative flex items-center justify-center flex-1">
            <svg
              width={ringSize}
              height={ringSize}
              className="transform -rotate-90"
            >
              {/* Background track */}
              <circle
                cx={ringSize / 2}
                cy={ringSize / 2}
                r={radius}
                fill="none"
                stroke="rgba(255, 255, 255, 0.4)"
                strokeWidth={strokeWidth}
              />
              {/* Progress arc */}
              <circle
                cx={ringSize / 2}
                cy={ringSize / 2}
                r={radius}
                fill="none"
                stroke="#20f219"
                strokeWidth={strokeWidth}
                strokeLinecap="round"
                strokeDasharray={circumference}
                strokeDashoffset={strokeDashoffset}
                className="transition-all duration-1000 ease-out"
              />
            </svg>
            {/* Percentage text */}
            <span className="absolute text-white text-[26px] font-medium">
              {displayedPercentage}%
            </span>
          </div>

          {/* Encouragement text */}
          <p className="text-white text-[9px] mt-2 text-center">
            {encouragement}
          </p>
        </div>

        {/* Right side - Challenge info + Stats card */}
        <div className="flex-1 flex flex-col ml-2">
          {/* Ongoing Challenge title + name */}
          <div className="mb-2 pl-1">
            <h4 className="text-white text-base font-semibold font-dm-sans leading-tight">
              Ongoing<br />
              Challenge #{challengeNumber}:
            </h4>
            <p className="text-[#20f219] text-sm font-medium font-dm-sans leading-tight mt-0.5">
              {name}
            </p>
          </div>

          {/* Green stats card - aligned to bottom */}
          <div className="flex-1 bg-[#20f219] rounded-tr-[15px] rounded-br-[15px] p-3 flex flex-col min-h-[110px]">
            {/* Fire icon */}
            <div className="mb-1">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M9.07533 22C9.07533 22 1.1291 19.3107 6.18069 9.0041C6.18069 9.0041 6.09248 12.5949 8.16123 13.7059C6.30898 10.5051 6.28974 4.275 11.8545 2C11.8545 2 9.93651 5.75626 16.3512 10.0467C22.7659 14.3372 17.9228 21.9044 14.6353 22C14.6353 22 17.6967 19.2894 15.0538 15.2327C15.0538 15.2327 15.2431 17.4394 14.0868 18.0389C14.0868 18.0389 15.126 14.0428 12.0277 11.6949C12.0277 11.6949 12.7782 13.8 9.89641 15.5681C7.0146 17.3362 7.47806 20.6219 9.07372 22H9.07533Z" fill="#202117"/>
              </svg>
            </div>

            {/* Calories value */}
            <div className="flex-1 flex flex-col justify-center">
              <span className="text-[#202117] text-[26px] font-medium leading-none">
                {estimatedCalories}
              </span>
              <div className="flex items-center gap-1 mt-1">
                <span className="text-[#202117]/50 text-[9px]">Kcal Burned</span>
                <span className="material-symbols-outlined text-[#202117]/50 text-[8px]">
                  info
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
      </div>
    </div>
  );
}
